import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import LeadForm from '@/components/internal/LeadForm';
import LeadKanban from '@/components/internal/LeadKanban';
import { useLeads, LEAD_STATUSES } from '@/hooks/useLeads';
import { formatDate, formatConsumption } from '@/utils/formatters';
import { Plus, Users, Filter, CheckCircle, XCircle, Search, ChevronRight, List, LayoutGrid, AlertCircle, RefreshCw } from 'lucide-react';

const statusClass = (s) => {
  const map = { neu: 'info', qualifiziert: 'primary', angebot_erstellt: 'primary', angebot_gesendet: 'info', angebot_geoeffnet: 'warning', in_verhandlung: 'warning', angenommen: 'success', vertrag: 'success', verloren: 'danger' };
  return map[s] || 'secondary';
};

const priorityClass = (p) => {
  const map = { niedrig: 'secondary', normal: 'primary', hoch: 'warning', dringend: 'danger' };
  return map[p] || 'secondary';
};

const LeadsList = () => {
  const navigate = useNavigate();
  // loadError = Daten fehlen. writeError = Daten sind da, eine Aenderung schlug fehl.
  // Nur loadError darf die Liste ausblenden.
  const { leads, loading, loadError, writeError, clearWriteError, stats, addLead, updateLeadStatus, reload } = useLeads();
  const [showNewModal, setShowNewModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('alle');
  const [search, setSearch] = useState('');
  const [view, setView] = useState(() => localStorage.getItem('aiqcon_leads_view') || 'kanban');
  // Fehler aus Schreibvorgaengen (Anlegen / Status aendern)
  const [formError, setFormError] = useState('');
  const [actionError, setActionError] = useState('');

  // Lokal abgefangener Fehler hat Vorrang; writeError faengt Pfade ab,
  // die hier nicht eigens behandelt werden — verschluckt wird keiner.
  const bannerError = actionError || writeError;
  const dismissBannerError = () => {
    setActionError('');
    clearWriteError();
  };

  const changeView = (v) => {
    setView(v);
    localStorage.setItem('aiqcon_leads_view', v);
  };

  const filteredLeads = leads.filter(l => {
    if (view === 'list' && statusFilter !== 'alle') {
      if (statusFilter === 'offen') { if (['vertrag', 'verloren'].includes(l.status)) return false; }
      else if (l.status !== statusFilter) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      const match = [l.company?.name, l.contact?.first_name, l.contact?.last_name, l.contact?.email, l.source].some(v => v && v.toLowerCase().includes(q));
      if (!match) return false;
    }
    return true;
  });

  const handleAddLead = async (data) => {
    setFormError('');
    try {
      await addLead(data);
      setShowNewModal(false);
    } catch (err) {
      // Modal bleibt offen, damit die Eingaben nicht verloren gehen.
      // Der Fehler wird im Dialog gezeigt — nicht zusaetzlich als Banner dahinter.
      setFormError(err.message || 'Lead konnte nicht angelegt werden');
      clearWriteError();
    }
  };

  // LeadKanban ruft ohne await auf — hier abfangen, sonst unbehandelte Rejection.
  const handleStatusChange = async (leadId, newStatus) => {
    setActionError('');
    try {
      await updateLeadStatus(leadId, newStatus);
    } catch (err) {
      setActionError(err.message || 'Status konnte nicht gespeichert werden');
    }
  };

  const openNewModal = () => { setFormError(''); setShowNewModal(true); };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="pg-title">Leads</h1>
          <p className="text-sm text-muted-foreground mt-1">Verwalten Sie Ihren Vertriebstrichter</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center bg-card border border-border rounded-lg p-1">
            <button
              onClick={() => changeView('kanban')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                view === 'kanban' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <LayoutGrid size={14} />
              Pipeline
            </button>
            <button
              onClick={() => changeView('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                view === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <List size={14} />
              Tabelle
            </button>
          </div>
          <button onClick={openNewModal} className="flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors shadow-sm">
            <Plus size={16} />Neuer Lead
          </button>
        </div>
      </div>

      {/* Serverfehler beim Laden — keine stillen Altdaten */}
      {loadError && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={18} className="text-destructive flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-destructive">Leads konnten nicht geladen werden</p>
            <p className="text-xs text-destructive/80 mt-0.5">{loadError}</p>
          </div>
          <button onClick={reload} className="flex items-center gap-1.5 text-xs font-semibold text-destructive hover:underline flex-shrink-0">
            <RefreshCw size={13} />Erneut versuchen
          </button>
        </div>
      )}

      {/* Fehler aus einer Statusaenderung — die Liste bleibt sichtbar */}
      {bannerError && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={18} className="text-destructive flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-destructive">Aenderung wurde nicht gespeichert</p>
            <p className="text-xs text-destructive/80 mt-0.5">{bannerError}</p>
          </div>
          <button onClick={dismissBannerError} className="text-xs font-semibold text-destructive hover:underline flex-shrink-0">
            Schliessen
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        {[
          { label: 'Gesamt', value: stats.total, icon: Users, color: 'bg-accent text-primary', filter: 'alle' },
          { label: 'Offen', value: stats.offen, icon: Filter, color: 'bg-[#fff8e0] text-[#b8860b]', filter: 'offen' },
          { label: 'Gewonnen', value: stats.angenommen, icon: CheckCircle, color: 'bg-[#e6fbf2] text-[#00a864]', filter: 'angenommen' },
          { label: 'Verloren', value: stats.verloren, icon: XCircle, color: 'bg-[#ffe6e6] text-[#cc0000]', filter: 'verloren' },
        ].map(s => (
          <div
            key={s.label}
            className={`stat-card ${view === 'list' ? 'cursor-pointer' : ''} ${view === 'list' && statusFilter === s.filter ? 'ring-2 ring-primary' : ''}`}
            onClick={() => view === 'list' && setStatusFilter(s.filter)}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-label">{s.label}</p>
                <p className="stat-value">{loading || loadError ? '–' : s.value}</p>
              </div>
              <div className={`stat-icon ${s.color}`}><s.icon size={20} /></div>
            </div>
          </div>
        ))}
      </div>

      {/* Search bar (both views) */}
      <div className="hk-card !py-0">
        <div className="flex items-center gap-3 px-4 py-3">
          <Search size={16} className="text-muted-foreground flex-shrink-0" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Suche nach Firma, Name oder E-Mail..."
            className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-muted-foreground"
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-xs text-muted-foreground hover:text-foreground">
              Zuruecksetzen
            </button>
          )}
          <span className="text-xs text-muted-foreground font-medium">
            {loading ? 'Lade …' : loadError ? '–' : `${filteredLeads.length} Leads`}
          </span>
        </div>
      </div>

      {/* Ladezustand — nichts vortaeuschen, solange der Server nicht geantwortet hat */}
      {loading && (
        <div className="hk-card">
          <p className="text-sm text-muted-foreground py-12 text-center">Lade Leads …</p>
        </div>
      )}

      {/* Kanban View */}
      {!loading && !loadError && view === 'kanban' && (
        <LeadKanban leads={filteredLeads} onStatusChange={handleStatusChange} />
      )}

      {/* List View */}
      {!loading && !loadError && view === 'list' && (
        <>
          {/* Filter Chips */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <button onClick={() => setStatusFilter('alle')} className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${statusFilter === 'alle' ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:border-primary hover:text-primary'}`}>
              Alle ({stats.total})
            </button>
            {LEAD_STATUSES.map(s => {
              const count = leads.filter(l => l.status === s.value).length;
              if (count === 0) return null;
              return (
                <button key={s.value} onClick={() => setStatusFilter(s.value)} className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${statusFilter === s.value ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:border-primary hover:text-primary'}`}>
                  {s.label} ({count})
                </button>
              );
            })}
          </div>

          {/* Table */}
          <div className="hk-card">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/30">
                  <tr className="text-left">
                    <th className="px-6 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Firma</th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Ansprechpartner</th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Verbrauch</th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Prioritaet</th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Naechste Aktion</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredLeads.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-12 text-muted-foreground text-sm">Keine Leads gefunden</td></tr>
                  ) : filteredLeads.map(lead => (
                    <tr key={lead.id} onClick={() => navigate(`/app/leads/${lead.id}`)} className="hover:bg-muted/30 cursor-pointer transition-colors group">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="hk-avatar size-sm">{lead.company?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}</div>
                          <div>
                            <p className="font-semibold text-foreground">{lead.company?.name}</p>
                            <p className="text-xs text-muted-foreground">{lead.company?.plz} {lead.company?.city}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-foreground">{lead.contact?.first_name} {lead.contact?.last_name}</p>
                        <p className="text-xs text-muted-foreground">{lead.contact?.email}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-medium text-foreground">{lead.company?.annual_consumption_kwh ? formatConsumption(lead.company.annual_consumption_kwh) : '–'}</span>
                      </td>
                      <td className="px-6 py-4"><span className={`hk-badge ${statusClass(lead.status)}`}>{lead.status.replace(/_/g, ' ')}</span></td>
                      <td className="px-6 py-4"><span className={`hk-badge ${priorityClass(lead.priority)}`}>{lead.priority}</span></td>
                      <td className="px-6 py-4">
                        {lead.next_action ? (
                          <div>
                            <p className="text-xs font-medium text-foreground truncate max-w-[180px]">{lead.next_action}</p>
                            {lead.next_action_date && <p className="text-[11px] text-muted-foreground">{formatDate(lead.next_action_date)}</p>}
                          </div>
                        ) : <span className="text-xs text-muted-foreground">–</span>}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <ChevronRight className="text-muted-foreground group-hover:text-primary transition-colors" size={18} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Neuer Lead</DialogTitle></DialogHeader>
          {formError && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 mb-2">
              <AlertCircle size={16} className="text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">{formError}</p>
            </div>
          )}
          <LeadForm onSave={handleAddLead} onCancel={() => setShowNewModal(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadsList;
